/* ============================================================
   STOCK REPORT · EOM EDITORIAL — downloadable PDF.
   Three levels: Item-Wise Sales, Raw Material Stock (deducted & valued), and
   Estimated vs Real-Time Usage. Provides the section catalogue + cover/contents
   to the shared editorialPdfEngine (block-level pagination + A4 rasterisation).
   Pairs with sales-report-export.css (shared base) + stock-report-export.css.
   ============================================================ */
import {
  formatINR, num, pct, esc, pad2, htmlItem, tableItem,
  mastheadHTML, downloadEditorialReport,
} from './editorialPdfEngine';
import '../components/stock-report-export.css';

export { formatINR };

/* Level-2 valuation, derived from the deducted rows so it always reconciles:
   Current − Deducted − Wasted = Remaining (each = Σ qty × unitCost). */
function valuation(d) {
  const rm = (d.rawMaterialDeducted || []).filter(r => (Number(r.deducted) || 0) > 0);
  return rm.reduce((a, r) => {
    const c = Number(r.unitCost) || 0;
    return {
      before: a.before + (Number(r.before) || 0) * c,
      deducted: a.deducted + (Number(r.deducted) || 0) * c,
      wasted: a.wasted + (Number(r.wasted) || 0) * c,
      after: a.after + (Number(r.after) || 0) * c,
    };
  }, { before: 0, deducted: 0, wasted: 0, after: 0 });
}

/* ---------- Level 1 · Item-Wise Sales ---------- */
function itemWiseFlow(d) {
  const iw = d.itemWiseSales || [];
  let iwSalesTotal = 0, gpTotal = 0, measurableSales = 0, qtyTotal = 0;
  const approxNames = [];
  // Rows whose printed figures lean on an old purchase price (C10).
  let estimatedCount = 0;
  const rows = iw.map(it => {
    const sales = Number(it.totalSales) || 0;
    qtyTotal += Number(it.qtySold) || 0;
    iwSalesTotal += sales;
    // Two different tags, deliberately. `approx` means the cost is UNKNOWN and the cells
    // dash. `~` means the figures printed on this row are real but partly priced from an
    // old purchase price — money that is IN the total and needs a label (C10).
    const tag = it.approximate
      ? ' <span class="approx">approx</span>'
      : (it.estimated ? ' <span class="approx">~ est</span>' : '');
    if (!it.approximate && it.estimated) estimatedCount += 1;
    let gpCell, mgCell;
    if (it.approximate) {
      approxNames.push(it.name);
      gpCell = `<td class="num dash">&mdash;</td>`;
      mgCell = `<td class="num dash">&mdash;</td>`;
    } else {
      const gp = Number(it.grossProfit) || 0;
      const mg = (it.margin != null) ? it.margin : (sales > 0 ? (gp / sales) * 100 : 0);
      gpTotal += gp; measurableSales += sales;
      gpCell = `<td class="num">${formatINR(gp)}</td>`;
      mgCell = `<td class="num">${pct(mg)}</td>`;
    }
    return `<tr><td class="name">${esc(it.name)}${tag}</td><td class="num">${num(it.qtySold)}</td><td class="num">${formatINR(it.sellingPrice)}</td><td class="num">${formatINR(sales)}</td>${gpCell}${mgCell}</tr>`;
  });
  const marginTotal = measurableSales > 0 ? (gpTotal / measurableSales) * 100 : 0;
  const star = approxNames.length ? ' *' : '';
  const tfoot = `<tr><td class="name">Total</td><td class="num">${num(qtyTotal)}</td><td class="num">&mdash;</td><td class="num">${formatINR(iwSalesTotal)}</td><td class="num">${formatINR(gpTotal)}${star}</td><td class="num">${pct(marginTotal)}${star}</td></tr>`;

  const items = [tableItem(
    `<tr><th>Item</th><th class="num">Qty Sold</th><th class="num">Selling Price</th><th class="num">Total Sales</th><th class="num">Gross Profit</th><th class="num">Margin %</th></tr>`,
    rows, tfoot
  )];
  // C10. The estimated-cost note, sized by the caller. A forwarded PDF is read by people
  // who never saw the setting that produced it, so this has to be IN the document — it is
  // the only thing that travels with the numbers.
  if (d.estimatedCogsNote) {
    items.push(htmlItem(`<p class="fnote">${esc(d.estimatedCogsNote)}</p>`));
  } else if (estimatedCount > 0) {
    // Belt and braces: if a caller ever passes marked rows without a note, the page still
    // says so rather than printing an unlabelled estimate.
    items.push(htmlItem(`<p class="fnote">~ ${estimatedCount} item(s) include ingredients priced at their last known purchase price rather than from stock actually held.</p>`));
  }
  if (approxNames.length) {
    const names = approxNames.map(n => `<strong>${esc(n)}</strong>`).join(' and ');
    items.push(htmlItem(`<p class="fnote">* Gross profit and margin are measured on the ${iw.length - approxNames.length} items with reliable cost. ${names} show &ldquo;&mdash;&rdquo; because an ingredient went into negative stock this period, so their cost &mdash; and therefore their profit &mdash; can't be trusted. Their sales are still counted; their profit is excluded from the total.</p>`));
  }
  items.push(htmlItem(`<div class="callout"><p class="k">How To Read A Row</p>
      <p><span class="eq">Selling Price &minus; Ingredient Cost &minus; Wastage = Gross Profit per plate.</span><br>
      When an ingredient drops into negative stock &mdash; as with any row marked <em>approx</em> &mdash; that item's cost can't be trusted, so Gross Profit and Margin show &ldquo;&mdash;&rdquo;.</p></div>`));
  return items;
}

/* ---------- Level 2 · Raw Material Stock (deducted only) ---------- */
function rawMaterialFlow(d) {
  const rm = (d.rawMaterialDeducted || []).filter(r => (Number(r.deducted) || 0) > 0);
  const v = valuation(d);
  const strip = `<div class="strip4">
      <div class="cell"><div class="l">Current Stock Value</div><div class="v">${formatINR(v.before)}</div></div>
      <div class="cell accent"><div class="l">Deducted Value</div><div class="v">${formatINR(v.deducted)}</div></div>
      <div class="cell"><div class="l">Wasted Value</div><div class="v">${formatINR(v.wasted)}</div></div>
      <div class="cell"><div class="l">Remaining Value</div><div class="v">${formatINR(v.after)}</div></div>
    </div>`;
  const rows = rm.map(r => {
    const c = Number(r.unitCost) || 0;
    const dedVal = (Number(r.deducted) || 0) * c;
    const remVal = (Number(r.after) || 0) * c;
    const u = esc(r.unit || '');
    return `<tr><td class="name">${esc(r.name)}</td><td class="num">${formatINR(c)}/${u}</td><td class="num">${num(r.before)} ${u}</td><td class="num">${num(r.deducted)} ${u}</td><td class="num">${formatINR(dedVal)}</td><td class="num">${num(r.after)} ${u}</td><td class="num">${formatINR(remVal)}</td></tr>`;
  });
  const tfoot = `<tr><td class="name">Total (${rm.length} deducted)</td><td class="num">&mdash;</td><td class="num">${formatINR(v.before)}</td><td class="num">&mdash;</td><td class="num">${formatINR(v.deducted)}</td><td class="num">&mdash;</td><td class="num">${formatINR(v.after)}</td></tr>`;
  return [
    htmlItem(strip),
    tableItem(
      `<tr><th>Ingredient</th><th class="num">Unit Cost</th><th class="num">Before</th><th class="num">Deducted</th><th class="num">Deducted Value</th><th class="num">Remaining</th><th class="num">Remaining Value</th></tr>`,
      rows, tfoot
    ),
    htmlItem(`<div class="callout"><p class="k">Reconciliation</p>
      <p><span class="eq">Current Stock Value &minus; Deducted Value &minus; Wasted Value = Remaining Value.</span><br>
      ${formatINR(v.before)} &minus; ${formatINR(v.deducted)} &minus; ${formatINR(v.wasted)} = ${formatINR(v.after)}. Each ingredient ties out the same way; a row that doesn't balance is flagged for a stock-count check. Ingredients with zero deduction this period are hidden.</p></div>`),
  ];
}

/* ---------- Level 3 · Estimated vs Real-Time Usage ---------- */
function estimatedFlow(d) {
  const ev = d.estimatedVsActual || [];
  const hasEv = ev.length > 0 && ev.some(r => (Number(r.estimated) || 0) > 0 || (Number(r.actual) || 0) > 0);
  if (!hasEv) {
    return [htmlItem(`<div class="callout empty"><div class="big">No estimations recorded for today.</div></div>`)];
  }
  let estT = 0, actT = 0;
  const rows = ev.map(r => {
    const e = Number(r.estimated) || 0, a = Number(r.actual) || 0, v = a - e;
    estT += e; actT += a;
    const cls = v >= 0 ? 'pos' : 'neg';
    const sign = v > 0 ? '+' : (v < 0 ? '&minus;' : '');
    return `<tr><td class="name">${esc(r.menuItem)}</td><td class="num">${num(e)}</td><td class="num">${num(a)}</td><td class="num ${cls}">${sign}${Math.abs(v)}</td></tr>`;
  });
  const vT = actT - estT;
  const clsT = vT >= 0 ? 'pos' : 'neg';
  const signT = vT > 0 ? '+' : (vT < 0 ? '&minus;' : '');
  const tfoot = `<tr><td class="name">Total</td><td class="num">${num(estT)}</td><td class="num">${num(actT)}</td><td class="num ${clsT}">${signT}${Math.abs(vT)}</td></tr>`;
  return [
    tableItem(
      `<tr><th>Menu Item</th><th class="num">Estimated</th><th class="num">Actual</th><th class="num">Variance</th></tr>`,
      rows, tfoot
    ),
    htmlItem(`<div class="callout"><p class="k">When There Is No Data</p>
      <p>If no estimations were recorded for the day, this section shows a single line &mdash; <span class="eq">&ldquo;No estimations recorded for today.&rdquo;</span> Variance keeps its + / &minus; sign in both the PDF and the Excel export.</p></div>`),
  ];
}

/* Section catalogue — ordered like the reference; ids match the modal's checkboxes. */
const SECTIONS = [
  { ids: ['item-wise'], kicker: 'Item-Wise Sales', title: 'Item-Wise Sales Report',
    contentsTitle: 'Item-Wise Sales Report', titleSm: true,
    lede: () => 'For every menu item sold in the period: how many sold, the revenue it earned, the gross profit left after ingredient cost and wastage, and the margin on it.',
    has: (d) => (d.itemWiseSales || []).length > 0, flow: itemWiseFlow },
  { ids: ['raw-material'], kicker: 'Raw Material Stock', title: 'Deducted Ingredients & Value',
    contentsTitle: 'Raw Material Stock — Deducted & Value', titleSm: true,
    lede: () => 'Only ingredients that were actually deducted this period, valued in rupees at their unit cost — showing what you started with, what was consumed, and what value remains.',
    has: (d) => (d.rawMaterialDeducted || []).some(r => (Number(r.deducted) || 0) > 0), flow: rawMaterialFlow },
  { ids: ['estimated'], kicker: 'Usage Variance', title: 'Estimated vs Real-Time Usage',
    contentsTitle: 'Estimated vs Real-Time Usage', titleSm: true,
    lede: () => 'Forecast plates against what actually sold, per menu item. A positive variance means more sold than expected; negative means fewer.',
    has: () => true, flow: estimatedFlow },
];

function coverHTML(d) {
  const m = d.meta;
  const v = valuation(d);
  return `
  <section class="page cover">
    <div class="cover-top">
      <div class="cover-brand">${esc(m.brand)}</div>
      <div class="cover-tag">${esc(m.title)}<br>Period · ${esc(m.period)}<br>Generated · ${esc(m.generated)}</div>
    </div>
    <div class="cover-mid">
      <div class="cover-kicker">${esc(m.kicker || 'Inventory Movement & Usage')}</div>
      <h1 class="cover-hero">${esc(m.heroLine1 || 'Stock')}<br>${esc(m.heroLine2 || 'Report')}</h1>
      <p class="cover-sub">${esc(m.subtitle || '')}</p>
      <div class="cover-strip">
        <div class="cell"><div class="l">Current Stock Value</div><div class="v">${formatINR(v.before)}</div></div>
        <div class="cell"><div class="l">Deducted Value</div><div class="v">${formatINR(v.deducted)}</div></div>
        <div class="cell"><div class="l">Remaining Value</div><div class="v">${formatINR(v.after)}</div></div>
      </div>
    </div>
    <div class="cover-foot"><span>Prepared for <strong>${esc(m.preparedFor || '')}</strong></span><span>${esc(m.generatedBy || 'Generated by Invexis')}</span></div>
  </section>`;
}

function contentsHTML(d, active) {
  const m = d.meta;
  const v = valuation(d);
  const rows = active.map((s, i) =>
    `<div class="idx-row"><span class="idx-num">${pad2(i + 1)}</span><span class="idx-title">${esc(s.contentsTitle || s.title)}</span><span class="idx-leader"></span><span class="idx-page">${pad2(i + 3)}</span></div>`
  ).join('');
  return `
  <section class="page">
    <div class="masthead">${mastheadHTML(m)}</div>
    <p class="kicker">Contents</p>
    <h2 class="sec-title">In This Report</h2>
    <hr class="rule">
    <div class="idx">${rows}</div>
    <div class="callout"><p class="k">How This Report Reconciles</p>
      <p>Level 2 ties out exactly: <span class="eq">Current Stock Value &minus; Deducted &minus; Wasted = Remaining</span> (${formatINR(v.before)} &minus; ${formatINR(v.deducted)} &minus; ${formatINR(v.wasted)} = ${formatINR(v.after)}). Level 1 shows how each dish converted sales into gross profit; Level 3 compares forecast plates to what actually sold.</p></div>
    <div class="pfoot"><span>${esc(m.generatedBy || 'Generated by Invexis')}</span><span>Contents</span></div>
  </section>`;
}

export async function downloadEditorialStockPdf(data, selectedSections, fileName) {
  const sel = new Set(selectedSections && selectedSections.length ? selectedSections : SECTIONS.flatMap(s => s.ids));
  const active = SECTIONS.filter(s => s.ids.some(id => sel.has(id)) && s.has(data));
  await downloadEditorialReport({
    meta: data.meta,
    coverHTML: coverHTML(data),
    contentsHTML: contentsHTML(data, active),
    sections: active,
    data,
    fileName,
  });
}

const stockReportEditorialPdf = { formatINR, downloadEditorialStockPdf };
export default stockReportEditorialPdf;
